import * as tslib_1 from "tslib";
import { Inject, Injectable } from '@angular/core';
import 'localforage';
import { InstanceFactory } from '../instance-factory/InstanceFactory.service';
import { addToStringTag } from '../util/addToStringTag';
import { NgForageConfig } from './NgForageConfig.service';
/**
 * Abstract service-level configuration layer for NgForage
 */
var BaseConfigurableImpl = (function () {
    /** @internal */
    function BaseConfigurableImpl(config, instanceFactory) {
        /** @internal */
        this.config = {};
        /** @internal */
        this.storeNeedsRecalc = true;
        this.baseConfig = config;
        this.fact = instanceFactory;
    }
    Object.defineProperty(BaseConfigurableImpl.prototype, "description", {
        /**
         * A description of the database, essentially for developer usage.
         * @default ""
         */
        get: function () {
            return 'description' in this.config ? this.config.description : this.baseConfig.description;
        },
        set: function (v) {
            this.config.description = v;
            this.storeNeedsRecalc = true;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseConfigurableImpl.prototype, "driver", {
        /**
         * The preferred driver(s) to use.
         * @see {@link NgForageConfig#DRIVER_INDEXEDDB}
         * @see {@link NgForageConfig#DRIVER_WEBSQL}
         * @see {@link NgForageConfig#DRIVER_LOCALSTORAGE}
         * @default IndexedDB, WebSQL and localStorage
         */
        get: function () {
            return 'driver' in this.config ? this.config.driver : this.baseConfig.driver;
        },
        set: function (v) {
            this.config.driver = v;
            this.storeNeedsRecalc = true;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseConfigurableImpl.prototype, "name", {
        /**
         * The name of the database. May appear during storage limit prompts. Useful to use the name of your app here.
         * In localStorage, this is used as a key prefix for all keys stored in localStorage.
         * @default ngForage
         */
        get: function () {
            return 'name' in this.config ? this.config.name : this.baseConfig.name;
        },
        set: function (v) {
            this.config.name = v;
            this.storeNeedsRecalc = true;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseConfigurableImpl.prototype, "size", {
        /**
         * The size of the database in bytes. Used only in WebSQL for now.
         * @default 4980736
         */
        get: function () {
            return 'size' in this.config ? this.config.size : this.baseConfig.size;
        },
        set: function (v) {
            this.config.size = v;
            this.storeNeedsRecalc = true;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseConfigurableImpl.prototype, "storeName", {
        /**
         * The name of the datastore.
         * In IndexedDB this is the dataStore,
         * in WebSQL this is the name of the key/value table in the database.
         * Must be alphanumeric, with underscores.
         * Any non-alphanumeric characters will be converted to underscores.
         * @default ng_forage
         */
        get: function () {
            return 'storeName' in this.config ? this.config.storeName : this.baseConfig.storeName;
        },
        set: function (v) {
            this.config.storeName = v;
            this.storeNeedsRecalc = true;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseConfigurableImpl.prototype, "version", {
        /**
         * The version of your database. May be used for upgrades in the future; currently unused.
         * @default 1.0
         */
        get: function () {
            return 'version' in this.config ? this.config.version : this.baseConfig.version;
        },
        set: function (v) {
            this.config.version = v;
            this.storeNeedsRecalc = true;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseConfigurableImpl.prototype, "store", {
        /** @internal */
        get: function () {
            if (this.storeNeedsRecalc || !this._store) {
                this._store = this.fact.getInstance(this.finalConfig);
                this.storeNeedsRecalc = false;
            }
            return this._store;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(BaseConfigurableImpl.prototype, "finalConfig", {
        /** @internal */
        get: function () {
            return Object.assign({}, this.baseConfig.config, this.config);
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Bulk-set configuration options
     * @param opts The configuration
     */
    BaseConfigurableImpl.prototype.configure = function (opts) {
        opts = opts || {};
        if ('driver' in opts && opts.driver.slice) {
            opts.driver = opts.driver.slice();
        }
        Object.assign(this.config, opts);
        this.storeNeedsRecalc = true;
        return this;
    };
    /** @internal */
    BaseConfigurableImpl.prototype.toJSON = function () {
        return {
            description: this.description,
            driver: this.driver,
            name: this.name,
            size: this.size,
            storeName: this.storeName,
            version: this.version
        };
    };
    BaseConfigurableImpl = tslib_1.__decorate([
        Injectable(),
        tslib_1.__param(0, Inject(NgForageConfig)),
        tslib_1.__param(1, Inject(InstanceFactory)),
        tslib_1.__metadata("design:paramtypes", [NgForageConfig,
            InstanceFactory])
    ], BaseConfigurableImpl);
    return BaseConfigurableImpl;
}());
export { BaseConfigurableImpl };
addToStringTag(BaseConfigurableImpl, 'BaseConfigurable');



//////////////////
// WEBPACK FOOTER
// ./src/NgForage/config/BaseConfigurableImpl.service.ts
// module id = 7
// module chunks = 0 1